import type { AnchorMeta, CorePort } from '../observerContext';
import { HALL_TOP } from './route';
import type { Box, HousePlan, PanePlan, ShellCard, TargetGeom } from './types';

/*
  План дома из разметки (spec-3-motion §1, §10): одним пакетом getBoundingClientRect в момент планирования —
  меню (CorePort.lanes), карточки рамки [data-shell-card] (menu, zone, pane), полосы вкладок, видимая часть
  прокрутки [data-scrollport], блоки [data-observer-block]. Числа в коде не зашиты: всё читается здесь, дальше
  маршрут считает чистыми функциями. Нет [data-shell] — дома нет (основной сайт пока без разметки).
*/

export const boxOf = (r: DOMRect | DOMRectReadOnly): Box => ({
  left: r.left,
  top: r.top,
  right: r.right,
  bottom: r.bottom,
});

/** Элемент на экране: в документе, ненулевой, не в спрятанной вкладке и не под inert. */
export function shown(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el.closest('[hidden], [inert]')) return false;
  const r = el.getBoundingClientRect();
  return r.width >= 1 && r.height >= 1;
}

const radiusOf = (el: Element) => parseFloat(getComputedStyle(el).borderTopRightRadius) || 0;

export interface PlanRead {
  plan: HousePlan;
  shell: HTMLElement;
  /** Окна по порядку плана (их элементы). */
  panes: HTMLElement[];
  /** Свой дом: кнопка знака или активный пункт — над ними капле можно быть у концов пути. */
  home: HTMLElement[];
}

export function readPlan(port: CorePort): PlanRead | null {
  const shell = document.querySelector<HTMLElement>('[data-shell]');
  const origin = port.origin();
  if (!shell || !origin) return null;
  const lanes = port.lanes();
  const cards: ShellCard[] = [];
  const paneEls: HTMLElement[] = [];
  const panes: PanePlan[] = [];
  for (const el of Array.from(shell.querySelectorAll<HTMLElement>('[data-shell-card]'))) {
    if (el.dataset.shellCard !== 'menu' && !shown(el)) continue;
    const kind = el.dataset.shellCard as ShellCard['kind'];
    // Уровень 2–3: метка на обёртке, скругление — у самой карточки внутри.
    const radius = kind === 'zone' && el.firstElementChild ? radiusOf(el.firstElementChild) : radiusOf(el);
    cards.push({ kind, box: boxOf(el.getBoundingClientRect()), radius });
    if (kind !== 'pane') continue;
    const strip = el.querySelector('[data-slot="tab-strip"]');
    const tabs = el.querySelector('[role="tablist"]');
    const scroll = Array.from(el.querySelectorAll<HTMLElement>('[data-scrollport]')).find((s) => !s.hidden);
    if (!strip || !tabs || !scroll) continue;
    paneEls.push(el);
    panes.push({
      box: boxOf(el.getBoundingClientRect()),
      strip: boxOf(strip.getBoundingClientRect()),
      tabs: boxOf(tabs.getBoundingClientRect()),
      scroll: boxOf(scroll.getBoundingClientRect()),
    });
  }
  cards.sort((a, b) => a.box.left - b.box.left);
  const M = boxOf(lanes.menu);
  const aside = shell.querySelector('[data-shell-card="menu"]');
  const brand = aside?.querySelector('[data-brand-home]')?.closest('button');
  const active = aside?.querySelector('[data-menu-item][aria-current="page"]');
  const home = [origin.kind === 'mark' ? brand : active].filter(
    (e): e is HTMLElement => e instanceof HTMLElement,
  );
  return {
    shell,
    panes: paneEls,
    home,
    plan: {
      ceiling: shell.getBoundingClientRect().top,
      menu: {
        box: M,
        hallX: lanes.hallX,
        sillY: lanes.sillY,
        hallTop: M.top + HALL_TOP,
        hallBottom: lanes.hallBottom,
        scroll: boxOf(lanes.scroll),
        items: lanes.items.map((i) => ({ id: i.id, box: boxOf(i.rect) })),
        sillGate: lanes.sillGate,
      },
      cards,
      panes,
      home: { kind: origin.kind, x: origin.x, y: origin.y },
    },
  };
}

/** Цель на плане: что за якорь, в каком окне, к какому блоку прислоняется строка. */
export interface TargetRead {
  geom: TargetGeom;
  /** Сам якорь (пункт, вкладка, блок или строка). */
  el: HTMLElement;
  /** Блок, на кромку которого садится тело (у строки — родитель). */
  block: HTMLElement | null;
  /** Строка внутри блока — ей контур. */
  row: HTMLElement | null;
  pane: HTMLElement | null;
  /** Прокрутка, в которой живёт цель: видимая часть — обрезка тела и проверка посадки. */
  scroll: HTMLElement | null;
}

export function readTarget(
  read: PlanRead,
  id: string,
  el: HTMLElement,
  meta: AnchorMeta,
): TargetRead | string {
  if (!shown(el)) return 'цель не на экране';
  if (id.startsWith('nav.') || meta.level === 0) {
    return { geom: { kind: 'menu', id }, el, block: null, row: null, pane: null, scroll: null };
  }
  const panel = el.closest<HTMLElement>('[data-shell-card="detail"]');
  if (panel) {
    const owner = panel.parentElement?.closest<HTMLElement>('[data-shell-card="pane"]') ?? null;
    const pane = owner ? read.panes.indexOf(owner) : -1;
    if (pane < 0) return 'панель вне окна';
    return {
      geom: { kind: 'panel', pane, box: boxOf(panel.getBoundingClientRect()) },
      el: panel,
      block: null,
      row: null,
      pane: owner,
      scroll: null,
    };
  }
  // Пункт меню приложения (уровень 3): капля встаёт в зазоре справа от его карточки — на стене окна.
  if (el.closest('[data-shell-card="zone"]')) {
    return {
      geom: { kind: 'zone', box: boxOf(el.getBoundingClientRect()) },
      el,
      block: null,
      row: null,
      pane: null,
      scroll: null,
    };
  }
  const paneEl = el.closest<HTMLElement>('[data-shell-card="pane"]');
  const pane = paneEl ? read.panes.indexOf(paneEl) : -1;
  if (!paneEl || pane < 0) return 'цель вне окон';
  if (el.getAttribute('role') === 'tab') {
    return {
      geom: { kind: 'tab', pane, box: boxOf(el.getBoundingClientRect()) },
      el,
      block: null,
      row: null,
      pane: paneEl,
      scroll: null,
    };
  }
  const scroll = el.closest<HTMLElement>('[data-scrollport]');
  if (!scroll || scroll.hidden) return 'цель не в видимой вкладке';
  if (meta.role === 'item') {
    const block = el.parentElement?.closest<HTMLElement>('[data-observer-block]') ?? null;
    if (block) {
      return {
        geom: { kind: 'block', pane, box: boxOf(block.getBoundingClientRect()) },
        el,
        block,
        row: el,
        pane: paneEl,
        scroll,
      };
    }
    return {
      geom: { kind: 'row', pane, box: boxOf(el.getBoundingClientRect()) },
      el,
      block: null,
      row: el,
      pane: paneEl,
      scroll,
    };
  }
  return {
    geom: { kind: 'block', pane, box: boxOf(el.getBoundingClientRect()) },
    el,
    block: el,
    row: null,
    pane: paneEl,
    scroll,
  };
}

/** Свойства, переход которых двигает раскладку: тогда ждём (spec-3-motion §4, раскладка в покое). */
const LAYOUT =
  /^(width|min-width|max-width|margin|margin-left|margin-right|transform|translate|scale|left|right|flex-basis)$/;

/** У меню, зоны и окон идут переходы, которые двигают раскладку. */
export function layoutMoving(shell: HTMLElement): boolean {
  const els = new Set<Element>();
  for (const el of Array.from(
    shell.querySelectorAll('[data-shell-card], [data-shell-zone], [data-shell-zone] > *'),
  )) {
    els.add(el);
    if (el.parentElement) els.add(el.parentElement);
  }
  for (const el of els) {
    for (const a of el.getAnimations()) {
      if (a instanceof CSSTransition && a.playState === 'running' && LAYOUT.test(a.transitionProperty))
        return true;
    }
  }
  return false;
}
